import { useState } from "react";
import { Fingerprint, Loader2, Save, Camera, Upload } from "lucide-react";
import { api, compressImage, formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { PossibleVariants } from "@/components/app/PossibleVariants";
import { ZoomablePreview } from "@/components/app/ZoomablePreview";

const KEY_LABELS = [
  ["ModelYear", "Model Year"],
  ["Make", "Make"],
  ["Model", "Model"],
  ["Trim", "Trim"],
  ["Series", "Series"],
  ["BodyClass", "Body Class"],
  ["VehicleType", "Vehicle Type"],
  ["EngineCylinders", "Cylinders"],
  ["DisplacementL", "Displacement (L)"],
  ["EngineHP", "Horsepower"],
  ["FuelTypePrimary", "Fuel"],
  ["TransmissionStyle", "Transmission"],
  ["DriveType", "Drive Type"],
  ["PlantCity", "Plant City"],
  ["PlantState", "Plant State"],
  ["PlantCountry", "Plant Country"],
  ["ManufacturerName", "Manufacturer"],
];

const MAKE_OPTIONS = [
  "", "Chevrolet", "Pontiac", "Buick", "Oldsmobile", "Cadillac",
  "Ford", "Mercury", "Lincoln", "Plymouth", "Dodge", "Chrysler",
  "Imperial", "AMC", "Studebaker", "Jaguar", "MG", "Triumph",
  "Mercedes-Benz", "Porsche", "Volkswagen", "Other",
];

const VinDecoder = () => {
  const [vin, setVin] = useState("");
  const [make, setMake] = useState("");
  const [ownerNotes, setOwnerNotes] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoReading, setPhotoReading] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  // User-selected variants from possible_variants (persists into garage)
  const [selectedVariants, setSelectedVariants] = useState(new Set());
  const toggleVariant = (name) => setSelectedVariants((prev) => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoFile(f);
    setPhotoReading(null);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const readPhoto = async (fileArg) => {
    const f = fileArg || photoFile;
    if (!f) return;
    setPhotoLoading(true);
    setData(null);
    setPhotoReading(null);
    try {
      const { base64, mime } = await compressImage(f, { maxDim: 1600, quality: 0.85 });
      const { data: resp } = await api.post("/vehicles/read-vin-photo", {
        image_base64: base64,
        mime_type: mime,
        make: make || null,
        auto_decode: true,
      });
      setPhotoReading(resp);
      if (resp.vin) setVin(resp.vin);
      if (resp.decode) setData(resp.decode);
      if (!resp.vin) toast.error("Couldn't read a VIN from that photo. Try a clearer, closer shot.");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPhotoLoading(false);
    }
  };

  const onCropVinPlate = async (f) => {
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
    setPhotoReading(null);
    await readPhoto(f);
  };

  const decode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setData(null);
    try {
      const { data: resp } = await api.post("/vehicles/decode-vin", {
        vin: vin.trim(),
        make: make || null,
        notes_from_owner: ownerNotes || null,
      });
      setData(resp);
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const n = data.nhtsa || {};
      const c = data.classic_decode?.decoded || {};
      await api.post("/vehicles/garage", {
        vin: data.vin,
        year: n.ModelYear || c.model_year || "",
        make: n.Make || c.make || "",
        model: n.Model || c.model || "",
        trim: n.Trim || c.body_style || "",
        vin_decoded: data,
        confirmed_options: [...selectedVariants],
      });
      toast.success("Filed in your garage");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const n = data?.nhtsa || {};
  const classic = data?.classic_decode?.decoded || null;
  const era = data?.era;
  const hasNhtsa = era === "modern" && Object.keys(n).length > 0;

  return (
    <div className="space-y-6">
      <div>
        <p className="font-mono text-xs tracking-widest text-amber-brass uppercase">// Station 02</p>
        <h1 className="font-display text-5xl uppercase text-cream mt-1">VIN Decoder</h1>
        <p className="text-secondary mt-2 max-w-2xl">
          Any era — modern 17-digit VINs pull from NHTSA vPIC. Short pre-1981 classic
          VINs (5–13 chars) get decoded position-by-position from maker-specific schemes.
        </p>
      </div>

      <form onSubmit={decode} className="panel p-6 space-y-3">
        <div className="grid md:grid-cols-[1fr,240px,auto] gap-3 items-end">
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-secondary">VIN</label>
            <input
              data-testid="vin-input"
              className="data-input mt-1 uppercase tracking-widest"
              placeholder="e.g. 124379N123456 (classic) or 1G1AY0876B5100001 (modern)"
              value={vin}
              onChange={(e) => setVin(e.target.value.toUpperCase().replace(/\s/g, ""))}
              required
              maxLength={17}
            />
          </div>
          <div>
            <label className="font-mono text-xs uppercase tracking-widest text-secondary">Make (helps classics)</label>
            <select
              data-testid="vin-make-select"
              className="data-input mt-1"
              value={make}
              onChange={(e) => setMake(e.target.value)}
            >
              {MAKE_OPTIONS.map((m) => (
                <option key={m} value={m}>{m === "" ? "— Let AI guess —" : m}</option>
              ))}
            </select>
          </div>
          <button data-testid="vin-decode-btn" disabled={loading || vin.length < 5} className="chrome-btn flex items-center justify-center gap-2">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Decoding…</> : <><Fingerprint size={16} /> Decode</>}
          </button>
        </div>
        <div>
          <label className="font-mono text-xs uppercase tracking-widest text-secondary">
            Owner Notes (Optional) — badges, options, plate info the AI can't see
          </label>
          <textarea
            data-testid="vin-owner-notes"
            className="data-input mt-1 font-body"
            rows={2}
            placeholder="e.g. Has GT badges, fog lamps, dual exhaust, GT gas cap. Door plate order code Y."
            value={ownerNotes}
            onChange={(e) => setOwnerNotes(e.target.value)}
          />
        </div>
      </form>

      {/* Photo upload — read VIN + data plate fields */}
      <div className="panel p-6">
        <div className="flex items-center justify-between mb-3">
          <span className="tag-chip">Or — Snap the Plate</span>
          <span className="font-mono text-[10px] uppercase tracking-widest text-secondary">Dash VIN · Door jamb · SPID label · Data plate</span>
        </div>
        <div className="grid md:grid-cols-[280px,1fr] gap-4 items-start">
          <label data-testid="vin-photo-dropzone" className="block border-2 border-dashed border-shop-strong hover:border-[var(--accent-amber)] transition-colors cursor-pointer p-4 text-center min-h-[160px] flex flex-col items-center justify-center">
            <input data-testid="vin-photo-input" type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPhoto} />
            {photoPreview ? (
              <ZoomablePreview
                src={photoPreview}
                alt="plate"
                testid="vin-plate-preview"
                className="max-h-40"
                onCrop={onCropVinPlate}
                cropLabel="Re-read this crop"
              />
            ) : (
              <div className="text-secondary">
                <Upload size={28} className="mx-auto text-amber-brass mb-2" />
                <p className="font-display uppercase tracking-widest text-cream text-sm">Upload plate photo</p>
                <p className="text-[10px] font-mono mt-1">JPG / PNG / WEBP</p>
              </div>
            )}
          </label>
          <div className="flex flex-col gap-3">
            <button
              data-testid="vin-photo-read-btn"
              onClick={() => readPhoto()}
              disabled={!photoFile || photoLoading}
              className="chrome-btn flex items-center justify-center gap-2 self-start"
            >
              {photoLoading ? <><Loader2 size={16} className="animate-spin" /> Reading plate…</> : <><Camera size={16} /> Read Plate & Decode</>}
            </button>

            {photoReading && (
              <div data-testid="vin-photo-result" className="border border-shop p-4 bg-elevated">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[10px] uppercase text-secondary">{photoReading.reading?.plate_type || "Plate"}</span>
                  {photoReading.reading?.vin_confidence && (
                    <span className="font-mono text-[10px] uppercase text-amber-brass">VIN confidence: {photoReading.reading.vin_confidence}</span>
                  )}
                </div>
                {photoReading.vin ? (
                  <div className="font-mono text-lg text-amber-brass tracking-widest break-all">{photoReading.vin}</div>
                ) : (
                  <div className="font-mono text-sm text-secondary">No VIN read.</div>
                )}
                {photoReading.reading?.plate_fields?.length > 0 && (
                  <div className="mt-3">
                    <div className="font-mono text-[10px] uppercase text-secondary mb-2">Data-plate fields</div>
                    <div className="border border-shop">
                      <table className="w-full text-xs">
                        <thead className="bg-panel">
                          <tr className="font-mono text-[10px] uppercase text-secondary">
                            <th className="text-left px-2 py-1.5">Code</th>
                            <th className="text-left px-2 py-1.5">Label</th>
                            <th className="text-left px-2 py-1.5">Meaning</th>
                          </tr>
                        </thead>
                        <tbody>
                          {photoReading.reading.plate_fields.map((f, i) => (
                            <tr key={i} className="border-t border-shop align-top">
                              <td className="px-2 py-1.5 font-mono text-amber-brass">{f.code}</td>
                              <td className="px-2 py-1.5 font-mono text-cream">{f.label}</td>
                              <td className="px-2 py-1.5 text-cream">{f.value}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {photoReading.reading?.ambiguous_chars?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <span className="font-mono text-[10px] uppercase text-secondary">Ambiguous:</span>
                    {photoReading.reading.ambiguous_chars.map((c, i) => (
                      <span key={i} className="tag-chip">{c}</span>
                    ))}
                  </div>
                )}
                {photoReading.reading?.notes && (
                  <p className="mt-2 text-xs italic text-secondary">{photoReading.reading.notes}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {data && (
        <div data-testid="vin-result" className="space-y-6">
          <div className="flex flex-wrap items-center gap-3">
            <span className="tag-chip">{era === "modern" ? "MODERN VIN (NHTSA)" : "CLASSIC VIN (AI DECODE)"}</span>
            <span className="font-mono text-xs text-secondary tracking-widest">{data.vin}</span>
            {classic?.confidence && (
              <span className="font-mono text-xs uppercase text-amber-brass">Confidence: {classic.confidence}</span>
            )}
            <button
              data-testid="vin-save-btn"
              onClick={save}
              disabled={saving}
              className="ghost-btn ml-auto flex items-center gap-2"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save to Garage
            </button>
          </div>

          {/* MODERN — NHTSA layout */}
          {era === "modern" && (
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 panel p-6">
                <span className="tag-chip">NHTSA vPIC</span>
                {hasNhtsa ? (
                  <div className="grid sm:grid-cols-2 gap-4 mt-5">
                    {KEY_LABELS.filter(([k]) => n[k]).map(([k, label]) => (
                      <div key={k} className="border-b border-shop pb-2">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-secondary">{label}</div>
                        <div className="font-mono text-sm text-cream mt-0.5">{n[k]}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-secondary mt-4 text-sm">NHTSA returned no structured data.</p>
                )}
                {hasNhtsa && (
                  <details className="mt-6">
                    <summary className="cursor-pointer font-mono text-xs uppercase text-amber-brass tracking-widest">Show All Fields</summary>
                    <div className="grid sm:grid-cols-2 gap-2 mt-3">
                      {Object.entries(n).map(([k, v]) => (
                        <div key={k} className="border-b border-shop/50 py-1">
                          <div className="font-mono text-[10px] uppercase text-secondary">{k}</div>
                          <div className="font-mono text-xs text-cream">{String(v)}</div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>

              <div className="panel p-6">
                <span className="tag-chip">AI Historian</span>
                <div className="mt-4 text-cream whitespace-pre-wrap font-body text-sm leading-relaxed">
                  {data.ai_summary}
                </div>
              </div>
            </div>
          )}

          {/* CLASSIC — AI position breakdown layout */}
          {era === "classic" && classic && (
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 panel p-6">
                <div className="flex items-center justify-between">
                  <span className="tag-chip">Position Breakdown</span>
                  <span className="font-mono text-xs text-secondary">{classic.vin_type || "classic"}</span>
                </div>

                <div className="mt-5">
                  <div className="font-display text-4xl uppercase text-cream leading-tight">
                    {classic.model_year} {classic.make}
                  </div>
                  <div className="font-display text-2xl uppercase text-red-candy leading-tight">
                    {classic.model} {classic.body_style && <span className="text-amber-brass">— {classic.body_style}</span>}
                  </div>
                </div>

                {classic.special_variant && (
                  <div className="mt-4 border-l-4 border-[var(--accent-red)] bg-[rgba(217,32,39,0.08)] px-4 py-3">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-red-candy">Special Variant</div>
                    <div className="font-display text-2xl uppercase text-cream mt-0.5">{classic.special_variant}</div>
                  </div>
                )}

                {classic.possible_variants?.length > 0 && (
                  <PossibleVariants
                    variants={classic.possible_variants}
                    selected={selectedVariants}
                    onToggleSelect={toggleVariant}
                  />
                )}

                {classic.position_breakdown?.length > 0 && (
                  <div className="mt-6 border border-shop">
                    <table className="w-full text-sm">
                      <thead className="bg-elevated">
                        <tr className="font-mono text-[10px] uppercase text-secondary">
                          <th className="text-left px-3 py-2 w-24">Position(s)</th>
                          <th className="text-left px-3 py-2 w-24">Code</th>
                          <th className="text-left px-3 py-2">Meaning</th>
                        </tr>
                      </thead>
                      <tbody>
                        {classic.position_breakdown.map((row, i) => (
                          <tr key={i} className="border-t border-shop align-top">
                            <td className="px-3 py-2 font-mono text-secondary">{row.positions}</td>
                            <td className="px-3 py-2 font-mono text-amber-brass">{row.code}</td>
                            <td className="px-3 py-2 text-cream">{row.meaning}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="panel p-6 space-y-4">
                <div>
                  <span className="tag-chip">Key Specs</span>
                  <dl className="mt-3 space-y-2 text-sm">
                    {classic.engine && (
                      <div>
                        <dt className="font-mono text-[10px] uppercase text-secondary">Engine</dt>
                        <dd className="font-mono text-cream">{classic.engine}</dd>
                      </div>
                    )}
                    {classic.assembly_plant && (
                      <div>
                        <dt className="font-mono text-[10px] uppercase text-secondary">Assembly Plant</dt>
                        <dd className="font-mono text-cream">{classic.assembly_plant}</dd>
                      </div>
                    )}
                    {classic.serial_number && (
                      <div>
                        <dt className="font-mono text-[10px] uppercase text-secondary">Serial No.</dt>
                        <dd className="font-mono text-cream">{classic.serial_number}</dd>
                      </div>
                    )}
                  </dl>
                </div>

                {classic.enthusiast_summary && (
                  <div>
                    <span className="tag-chip">Historian Note</span>
                    <p className="mt-2 text-sm text-cream leading-relaxed">{classic.enthusiast_summary}</p>
                  </div>
                )}

                {classic.notes && (
                  <div>
                    <div className="font-mono text-[10px] uppercase text-secondary mb-1">Caveats</div>
                    <p className="text-xs text-secondary italic">{classic.notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {era === "classic" && !classic && data.classic_decode?.raw && (
            <pre className="panel p-4 text-xs text-secondary whitespace-pre-wrap">{data.classic_decode.raw}</pre>
          )}
        </div>
      )}
    </div>
  );
};

export default VinDecoder;
